import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  isEscalationOverdue,
  useAcknowledgeEscalationMutation,
  type DeviationSummary,
} from '@/lib/api/deviations';
import { formatDateTime } from '@/lib/utils/format';

/**
 * Escalation SLA banner (ported pattern). Deviations get a severity-based
 * escalation deadline at creation (Critical +8h, High +24h, Medium next
 * Monday 09:00). Anything past due and unacknowledged surfaces here until
 * a lead acknowledges it — acknowledgement is recorded with actor +
 * timestamp in the audit trail.
 */
export function EscalationBanner({
  engagementId,
  items,
}: {
  engagementId: string;
  items: ReadonlyArray<DeviationSummary>;
}) {
  const acknowledge = useAcknowledgeEscalationMutation(engagementId);
  const overdue = items.filter((d) => isEscalationOverdue(d));
  if (overdue.length === 0) return null;

  return (
    <div
      role="alert"
      className="rounded-card border border-posture-critical/40 bg-posture-critical/5 p-4"
    >
      <p className="flex items-center gap-2 text-body-sm font-semibold text-posture-critical">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        {overdue.length} deviation{overdue.length === 1 ? '' : 's'} past the escalation deadline
      </p>
      <ul className="mt-2 space-y-1.5">
        {overdue.map((d) => (
          <li
            key={d.deviation_entry_id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-card bg-paper-0 px-3 py-2 text-meta text-ink-700"
          >
            <span className="min-w-0 flex-1 truncate">
              <strong>{d.severity}</strong> · {d.title}
              <span className="ml-2 text-ink-500">
                due {d.escalation_due ? formatDateTime(d.escalation_due) : '—'}
              </span>
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={acknowledge.isPending}
              onClick={() => acknowledge.mutate(d.deviation_entry_id)}
            >
              Acknowledge
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
